import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CandidateProfileSection } from './CandidateProfileSection';
import { CandidateReviewSummary } from './CandidateReviewSummary';
import type { ContactsByExperience } from '../utils/contact-results';
import type { Experience } from '@/features/experiences/types/experience.types';

export type MissingProfileRequirement = 'experience' | 'reference-contact' | null;

interface CandidateReviewStepProps {
  missingRequirement: MissingProfileRequirement;
  onBack: () => void;
  experiences: Experience[];
  contactsByExperience: ContactsByExperience;
}

const MISSING_MESSAGES: Record<Exclude<MissingProfileRequirement, null>, string> = {
  experience: 'Add a work experience before submitting',
  'reference-contact': 'Add a reference contact for this role before submitting',
};

function MissingGuidance({
  requirement,
  onBack,
}: {
  requirement: Exclude<MissingProfileRequirement, null>;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="space-y-3 text-center">
      <p className="font-medium">{MISSING_MESSAGES[requirement]}</p>
      <Button variant="outline" onClick={onBack}>
        Back to experience & references
      </Button>
    </div>
  );
}

export function CandidateReviewStep({
  missingRequirement,
  onBack,
  experiences,
  contactsByExperience,
}: CandidateReviewStepProps): JSX.Element {
  const [isReviewing, setIsReviewing] = useState(false);

  if (missingRequirement) {
    return <MissingGuidance requirement={missingRequirement} onBack={onBack} />;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1 text-center">
        <p className="font-medium">Your profile is ready for review</p>
        <p className="text-sm text-muted-foreground">
          You can still go back and edit any information.
        </p>
      </div>
      <div className="flex justify-center">
        <Button onClick={(): void => setIsReviewing(!isReviewing)}>Review profile</Button>
      </div>
      {isReviewing && (
        <div className="space-y-6">
          <CandidateReviewSummary
            experiences={experiences}
            contactsByExperience={contactsByExperience}
          />
          <CandidateProfileSection />
        </div>
      )}
    </div>
  );
}
